/* eslint-disable no-undef */
/* =============================================================================
 * Invoices — history grid + QuickBooks-style invoice detail drawer
 *   · Grid is LIVE (real API) with a mock fallback so it never goes blank.
 *   · LiveInvoiceDrawer renders API detail; InvoiceDrawer (below) stays on
 *     mock DATA so the app shell can keep using it.
 * ===========================================================================*/
import React, { useState, useEffect, useRef } from "react";
import { fmtMoney, fmtMoney0, fmtNum, fmtHcf, fmtDate, fmtShort, initials, mgrColor, Icon, StatusPill, Pill, Avatar, Drawer, DRow, Search, useToast, ApiDown } from "./components.jsx";
import { DATA } from "./data.js";
import { fetchInvoices, fetchInvoiceDetail, mapInvoice, mapInvoiceDetail, sendQboInvoice, qboEmailInvoice, fetchQboStatus, fetchBillPreview, commitBillRun, mapBillRow, fetchQboConnections, refreshInvoicePayments } from "./api.js";

// StatusPill only styles Paid / Sent / Overdue sensibly; Draft & Void fall back
// to a plain muted pill (StatusPill lives in components.jsx and must not change).
const KNOWN_STATUS = { Paid: 1, Open: 1, Pushed: 1, Sent: 1, Overdue: 1 };
function InvStatus({ status }) {
  if (KNOWN_STATUS[status]) return <StatusPill status={status} />;
  return <Pill kind="muted">{status}</Pill>;
}

function Invoices({ mgr, params }) {
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [sort, setSort] = useState({ k: "date", dir: -1 });
  const [live, setLive] = useState(null);        // array = live rows · false = fetch failed · null = loading
  const [detail, setDetail] = useState(null);    // mapped detail · false = error · null = loading (drawer)
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);   // Create-invoices drawer
  const [sel, setSel] = useState(() => new Set());       // selected DRAFT invoice ids (for bulk send)
  const [bulkBusy, setBulkBusy] = useState(false);
  const [refresh, setRefresh] = useState(0);
  const [qbo, setQbo] = useState(null);          // /api/qbo/status — emailEnabled gates the email-on-push option
  const paySynced = useRef(null);                // mgr scope whose payment statuses were already refreshed this visit
  const toast = useToast();

  useEffect(() => { fetchQboStatus().then(setQbo).catch(() => setQbo(null)); }, []);

  // Fetch the list on mount and whenever the selected manager (or a create/send) changes.
  useEffect(() => {
    let cancelled = false;
    setLive(null); setSel(new Set());
    fetchInvoices(mgr === "all" ? null : mgr)
      .then((rows) => {
        if (cancelled) return;
        setLive(rows.map(mapInvoice));
        // R7: after the FIRST load for this scope, ask the API to pull payment status (Paid / Overdue /
        // Void) from QuickBooks; when anything changed, re-fetch so the pills repaint. Once per mgr change
        // (refresh bumps don't re-sync) and deliberately quiet — no spinner, no toast on 0 updates.
        if (paySynced.current !== mgr) {
          paySynced.current = mgr;
          refreshInvoicePayments(mgr !== "all" ? mgr : null)
            .then((r) => {
              if (!r || !(r.updated > 0)) return;
              // Refetch even if THIS run was superseded (scope switch mid-poll): the statuses changed
              // server-side and the new scope's list may have been fetched before they applied. The
              // component is still mounted; the bump refetches the CURRENT scope. Only the toast is
              // gated — once per actual sync (firstDelivery), and not for a scope the user left.
              setRefresh((n) => n + 1);
              if (!cancelled && r.firstDelivery)
                toast({ title: `Payment statuses updated (${r.updated})`, body: `${r.updated} invoice${r.updated === 1 ? "" : "s"} changed status in QuickBooks.` });
            })
            .catch(() => { /* silent — statuses simply stay as-was */ });
        }
      })
      .catch(() => { if (!cancelled) setLive(false); });
    return () => { cancelled = true; };
  }, [mgr, refresh]);
  // Deep-link: /invoices?create → open the Create-invoices drawer straight away.
  useEffect(() => { if (params && params.create) setCreateOpen(true); }, [params && params.create]);

  const usingLive = Array.isArray(live);

  // If navigated here from a unit with a specific invoice, open its drawer once live loads.
  useEffect(() => {
    if (usingLive && params && params.invoiceId) {
      setDrawerOpen(true); setDetail(null);
      fetchInvoiceDetail(params.invoiceId).then((d) => setDetail(mapInvoiceDetail(d))).catch(() => setDetail(false));
    }
  }, [usingLive, params && params.invoiceId]);

  // Property / unit cascade filter (unit activates once a property is chosen; both reset with scope).
  const [propF, setPropF] = useState("all");
  const [unitF, setUnitF] = useState("all");
  // Tenant-activity scope: day-to-day is ACTIVE tenants; former tenants' (imported) history is opt-in.
  const [tenantF, setTenantF] = useState("active");
  useEffect(() => { setPropF("all"); setUnitF("all"); setTenantF("active"); }, [mgr]);
  // The cascade's options derive from the tenant scope — switching Active/Former/All would strand
  // property/unit selections on options that no longer exist, so scope changes reset the chain.
  useEffect(() => { setPropF("all"); setUnitF("all"); }, [tenantF]);

  // Deployed builds: an API failure must be an honest error, not mock data that looks live.
  // Placed after the deep-link useEffect above so every hook runs on every render.
  if (!live && !import.meta.env.DEV) return <ApiDown loading={live !== false} />;

  // Live rows carry their own display fields; mock rows resolve via DATA. Normalise
  // both into a common accessor so search / render never has to branch.
  const norm = (i) => usingLive
    ? { tenant: i.tenant, unitName: i.unitName, managerName: i.managerName, propName: i.propName || "" }
    : (() => { const u = DATA.unit(i.unitId) || {}; const m = DATA.manager(i.managerId) || {}; return { tenant: u.tenant || "", unitName: u.name || "", managerName: m.name || "", propName: u.propName || "" }; })();

  const base = usingLive ? live : DATA.invoicesFor(mgr);

  // Tenant-activity scope first (mock rows have no flag and count as active), then the cascade.
  const tenantScoped = tenantF === "all" ? base
    : base.filter((i) => (i.tenantActive !== false) === (tenantF === "active"));

  // Cascade options come from the tenant-scoped rows — with "All tenants" a unit filter still shows
  // its complete billing history across every tenancy it ever had.
  const propOptions = [...new Set(tenantScoped.map((i) => norm(i).propName).filter(Boolean))].sort();
  const unitOptions = propF === "all" ? []
    : [...new Set(tenantScoped.filter((i) => norm(i).propName === propF).map((i) => norm(i).unitName).filter(Boolean))].sort();
  const scoped = propF === "all" ? tenantScoped
    : tenantScoped.filter((i) => { const n = norm(i); return n.propName === propF && (unitF === "all" || n.unitName === unitF); });

  let invs = scoped;
  if (status !== "all") invs = invs.filter((i) => i.status === status || (status === "Open" && i.status === "Sent"));   // "Sent" = mock compat, same as the chip counts
  if (q) {
    const s = q.toLowerCase();
    invs = invs.filter((i) => {
      const n = norm(i);
      return String(i.number).toLowerCase().includes(s) || n.tenant.toLowerCase().includes(s) || n.unitName.toLowerCase().includes(s);
    });
  }
  invs = invs.slice().sort((a, b) => {
    let av, bv;
    if (sort.k === "date") { av = a.date; bv = b.date; }
    else if (sort.k === "total") { av = a.total; bv = b.total; }
    else if (sort.k === "usage") { av = a.usage; bv = b.usage; }
    else { av = a.number; bv = b.number; }
    return (av > bv ? 1 : av < bv ? -1 : 0) * sort.dir;
  });

  const counts = { all: scoped.length };   // tab counts follow the property/unit scope, like the list
  ["Draft", "Paid", "Open", "Overdue"].forEach((s) => counts[s] = scoped.filter((i) => i.status === s || (s === "Open" && i.status === "Sent")).length);   // "Sent" = mock-data compat
  // Outstanding = submitted and unpaid (Sent + Overdue). Drafts aren't receivables yet — they haven't been
  // sent to anyone — counting them here made the header claim $6k "outstanding" on a book of unsent drafts.
  const totalOutstanding = base.filter((i) => i.status === "Open" || i.status === "Sent" || i.status === "Overdue").reduce((s, i) => s + i.total, 0);

  // Bulk actions operate on Draft invoices (only drafts can be sent to QuickBooks).
  const draftIds = invs.filter((i) => i.status === "Draft").map((i) => i.id);   // visible drafts (drives select-all)
  const selDrafts = base.filter((i) => i.status === "Draft" && sel.has(i.id));  // all selected drafts (survive filtering)
  const toggleSel = (id) => setSel((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleSelAll = () => setSel((s) => draftIds.length && draftIds.every((id) => s.has(id)) ? new Set() : new Set(draftIds));
  const bulkSend = async () => {
    setBulkBusy(true);
    let sent = 0, fail = 0;
    for (const inv of selDrafts) {
      try { const r = await sendQboInvoice(inv.id); r.sent ? sent++ : fail++; } catch { fail++; }
    }
    setBulkBusy(false); setSel(new Set()); setRefresh((n) => n + 1);
    toast({ title: sent ? "Sent to QuickBooks" : "Nothing sent", body: `${sent} invoice${sent === 1 ? "" : "s"} sent${fail ? ` · ${fail} blocked or failed (realm not allow-listed?)` : ""}.`, err: sent === 0 });
  };

  // Row click opens the self-contained drawer off the API detail.
  const onRow = (inv) => {
    setDrawerOpen(true);
    setDetail(null);
    fetchInvoiceDetail(inv.id).then((d) => setDetail(mapInvoiceDetail(d))).catch(() => setDetail(false));
  };
  const closeDrawer = () => { setDrawerOpen(false); setDetail(null); };
  // Silent re-fetch of the open invoice (e.g. after an email send stamps EmailedUtc) — keeps the drawer current.
  const refreshDetail = () => { if (detail && detail.id) fetchInvoiceDetail(detail.id).then((d) => setDetail(mapInvoiceDetail(d))).catch(() => {}); };

  // Export the currently-filtered invoices to a CSV the browser downloads (usage is in HCF).
  const exportCsv = () => {
    const head = ["Invoice", "Tenant", "Unit", "Manager", "Date", "Period start", "Period end", "Usage (HCF)", "Total", "Status", "Delivery"];
    const rows = invs.map((i) => {
      const n = norm(i);
      return [i.number, n.tenant, n.unitName, n.managerName, fmtShort(i.date), fmtShort(i.periodStart), fmtShort(i.periodEnd),
        i.usage, i.total, i.status, i.autoPay ? "Auto-pay" : i.emailed ? "Email" : "Print"];
    });
    const csv = [head, ...rows].map((r) => r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(",")).join("\r\n");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
    a.download = "invoices.csv"; a.click(); URL.revokeObjectURL(a.href);
    toast({ title: "Exported", body: `${invs.length} invoice${invs.length === 1 ? "" : "s"} exported to invoices.csv.` });
  };

  const Th = ({ k, children, num }) => (
    <th className={`sortable ${num ? "num" : ""}`} onClick={() => setSort((s) => ({ k, dir: s.k === k ? -s.dir : -1 }))}>
      {children}{sort.k === k && <span className="sortarrow">{sort.dir === 1 ? "▲" : "▼"}</span>}
    </th>
  );

  return (
    <React.Fragment>
    <div className="page">
      <div className="page__head">
        <div>
          <h1>Invoices</h1>
          <p>{base.length} invoices · {fmtMoney0(totalOutstanding)} outstanding{counts.Draft > 0 ? ` · ${counts.Draft} draft${counts.Draft === 1 ? "" : "s"}` : ""}{usingLive ? " · Live (API)" : live === false ? " · mock (API offline)" : ""}</p>
        </div>
        <div className="right">
          {usingLive && <Pill kind="ok">Live (API)</Pill>}
          <button className="btn btn--ghost" onClick={exportCsv}><Icon name="download" size={16} />Export</button>
          <button className="btn btn--primary" onClick={() => setCreateOpen(true)}><Icon name="plus" size={16} />Create invoices</button>
        </div>
      </div>

      <div className="tbl-wrap">
        {selDrafts.length > 0 && (
          <div className="between" style={{ padding: "10px 14px", background: "var(--color-primary-bg)", borderBottom: "1px solid var(--color-border-light)" }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: "var(--color-primary)" }}>{selDrafts.length} draft{selDrafts.length === 1 ? "" : "s"} selected · {fmtMoney(selDrafts.reduce((s, i) => s + i.total, 0))}</span>
            <span className="row" style={{ gap: 8 }}>
              <button className="btn btn--ghost btn--sm" onClick={() => setSel(new Set())}>Clear</button>
              <button className="btn btn--primary btn--sm" disabled={bulkBusy} onClick={bulkSend}><Icon name="send" size={14} />{bulkBusy ? "Sending…" : `Send ${selDrafts.length} to QuickBooks`}</button>
            </span>
          </div>
        )}
        <div className="tbl-tools">
          <Search value={q} onChange={setQ} placeholder="Search invoice #, tenant, unit…" />
          <select className="input input--sm" style={{ width: 150 }} value={propF} title="Filter by property"
                  onChange={(e) => { setPropF(e.target.value); setUnitF("all"); }}>
            <option value="all">All properties</option>
            {propOptions.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
          <select className="input input--sm" style={{ width: 110 }} value={unitF} disabled={propF === "all"}
                  title={propF === "all" ? "Pick a property first" : "Filter by unit"}
                  onChange={(e) => setUnitF(e.target.value)}>
            <option value="all">All units</option>
            {unitOptions.map((u) => <option key={u} value={u}>Unit {u}</option>)}
          </select>
          <select className="input input--sm" style={{ width: 160 }} value={tenantF}
                  title="Which tenancies' invoices to show — former tenants' imported history is hidden by default"
                  onChange={(e) => setTenantF(e.target.value)}>
            <option value="active">Active tenants</option>
            <option value="former">Former tenants</option>
            <option value="all">All tenants</option>
          </select>
          <div className="seg">
            {[["all", `All ${counts.all}`], ["Draft", `Draft ${counts.Draft}`], ["Open", `Open ${counts.Open}`], ["Overdue", `Overdue ${counts.Overdue}`], ["Paid", `Paid ${counts.Paid}`]].map(([k, l]) => (
              <button key={k} className={status === k ? "on" : ""} onClick={() => setStatus(k)}>{l}</button>
            ))}
          </div>
          <div className="spacer" />
          <span className="count">{invs.length} shown</span>
        </div>
        <table className="tbl">
          <thead>
            <tr>
              <th style={{ width: 30 }} onClick={(e) => e.stopPropagation()}>
                <input type="checkbox" checked={draftIds.length > 0 && draftIds.every((id) => sel.has(id))} disabled={!draftIds.length} onChange={toggleSelAll} aria-label="Select all drafts" />
              </th>
              <Th k="number">Invoice</Th>
              <th>Tenant / Unit</th>
              {mgr === "all" && <th>Manager</th>}
              <Th k="date">Date</Th>
              <th>Period</th>
              <Th k="usage" num>Usage</Th>
              <Th k="total" num>Total</Th>
              <th>Status</th>
              <th>Delivery</th>
              <th style={{ width: 30 }}></th>
            </tr>
          </thead>
          <tbody>
            {invs.map((inv) => {
              const n = norm(inv);
              return (
                <tr key={inv.id} onClick={() => onRow(inv)}>
                  <td onClick={(e) => e.stopPropagation()}>{inv.status === "Draft" && <input type="checkbox" checked={sel.has(inv.id)} onChange={() => toggleSel(inv.id)} aria-label={`Select invoice ${inv.number}`} />}</td>
                  <td className="strong">#{inv.number}</td>
                  <td><div className="cell-2"><b>{n.tenant}</b><span>{n.unitName}</span></div></td>
                  {mgr === "all" && (
                    <td>
                      <span className="row" style={{ gap: 7 }}>
                        <Avatar id={usingLive ? inv.id : inv.managerId} label={initials(n.managerName)} size={20} round={5} />
                        <span className="muted" style={{ fontSize: 12 }}>{(n.managerName || "").split(" ")[0]}</span>
                      </span>
                    </td>
                  )}
                  <td className="muted">{fmtShort(inv.date)}</td>
                  <td className="muted" style={{ fontSize: 11.5 }}>{fmtShort(inv.periodStart)} → {fmtShort(inv.periodEnd)}</td>
                  <td className="num">{inv.usage}{inv.spike && <span style={{ color: "var(--color-danger)" }} title="spike"> ⚠</span>}</td>
                  <td className="num strong">{fmtMoney(inv.total)}</td>
                  <td><InvStatus status={inv.status} /></td>
                  {/* Drafts show the delivery PREFERENCE; pushed invoices show what actually happened —
                      same truth as the drawer's Emailed/Not-emailed chip. */}
                  <td>{inv.autoPay ? <Pill kind="scheduled">Auto-pay</Pill>
                    : inv.status === "Draft"
                      ? (inv.emailed ? <span title="Email preference — emails are only sent when pushing"><Pill kind="muted">Email pref</Pill></span> : <Pill kind="muted">Print</Pill>)
                      : inv.emailedUtc ? <span title={`Emailed ${fmtShort(inv.emailedUtc)}`}><Pill kind="ok">Emailed</Pill></span> : <Pill kind="muted">Not emailed</Pill>}</td>
                  <td onClick={(e) => e.stopPropagation()}><button className="rowmenu" onClick={() => onRow(inv)}><Icon name="chevron" size={16} /></button></td>
                </tr>
              );
            })}
            {invs.length === 0 && <tr><td colSpan={mgr === "all" ? 11 : 10}><div className="empty">{live === null ? "Loading invoices…" : "No invoices match your filter."}</div></td></tr>}
          </tbody>
        </table>
      </div>
    </div>
      <LiveInvoiceDrawer open={drawerOpen} inv={detail} qbo={qbo} onClose={closeDrawer} onRefresh={refreshDetail} />
      <CreateInvoicesDrawer open={createOpen} mgr={mgr} unitId={params && params.unitId} initialDate={params && params.readDate} onClose={() => setCreateOpen(false)} onDone={() => setRefresh((n) => n + 1)} />
    </React.Fragment>
  );
}

// ------------------------------------------------ Live invoice drawer (API data)
// Same QBO-style card as InvoiceDrawer, but reads the mapped-detail shape and does
// NO DATA lookups. `inv`: object = detail · null = loading · false = error.
function LiveInvoiceDrawer({ open, inv, qbo, onClose, onRefresh }) {
  const toast = useToast();
  const [sending, setSending] = useState(false);
  const [emailing, setEmailing] = useState(false);
  const emailEnabled = !!(qbo && qbo.emailEnabled);
  const [emailOnPush, setEmailOnPush] = useState(false);
  // Default = the tenancy's auto-email preference, but only when email delivery is on in this environment.
  useEffect(() => { setEmailOnPush(!!(inv && inv.emailed) && emailEnabled); }, [inv && inv.id, emailEnabled]);
  if (!open) return null;
  const ready = inv && inv !== true;
  const send = () => {
    setSending(true);
    sendQboInvoice(inv.id, emailOnPush)
      .then((r) => {
        setSending(false);
        const emailBit = emailOnPush && r.emailNote ? ` · ${r.emailNote}` : emailOnPush && r.emailed ? " · Invoice emailed via QuickBooks." : "";
        toast({ title: r.sent ? `Sent to QuickBooks — doc #${r.docNumber}` : "Not sent", body: (r.note || "") + emailBit });
      })
      .catch(() => { setSending(false); toast({ title: "Send failed", body: "The API rejected the send." }); });
  };
  const emailNow = () => {
    setEmailing(true);
    qboEmailInvoice(inv.id)
      .then((r) => {
        setEmailing(false);
        toast({ title: r.emailed ? "Invoice emailed" : "Not emailed", body: r.note || "", err: !r.emailed });
        if (r.emailed && onRefresh) onRefresh();
      })
      .catch(() => { setEmailing(false); toast({ title: "Email failed", body: "The API rejected the request.", err: true }); });
  };

  return (
    <Drawer open={open} onClose={onClose}>
      <div className="drawer__head">
        <div className="av av-sq" style={{ background: "var(--accent)" }}><Icon name="receipt" size={20} /></div>
        <div style={{ flex: 1 }}>
          <h2>{ready ? `Invoice #${inv.number}` : "Loading…"}</h2>
          <div className="sub">{ready ? `${inv.managerName} · ${fmtDate(inv.date)}` : ""}</div>
          {ready && (
            <div className="wrap-gap" style={{ marginTop: 9 }}>
              <Pill kind="ok">Live</Pill>
              <InvStatus status={inv.status} />
              {inv.status === "Draft"
                ? (inv.autoPay ? <Pill kind="scheduled">Auto-pay</Pill> : inv.emailed ? <span title="Email preference — emails are only sent when pushing"><Pill kind="ok">Email</Pill></span> : <Pill kind="muted">Print</Pill>)
                : (inv.emailedUtc ? <Pill kind="ok">Emailed {fmtShort(inv.emailedUtc)}</Pill> : <Pill kind="muted">Not emailed</Pill>)}
            </div>
          )}
        </div>
        <button className="drawer__close" onClick={onClose}>&times;</button>
      </div>

      <div className="drawer__body">
        {inv === null && <div className="empty">Loading invoice detail…</div>}
        {inv === false && <div className="empty">Couldn't load invoice detail from the API.</div>}
        {ready && (
          <React.Fragment>
            {/* QBO-style invoice card */}
            <div className="card" style={{ boxShadow: "none", border: "1px solid var(--color-border-light)", padding: 0, overflow: "hidden" }}>
              <div style={{ padding: "16px 18px", borderBottom: "1px solid var(--color-border-light)", display: "flex", justifyContent: "space-between" }}>
                <div>
                  <div className="muted" style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: ".5px" }}>Bill to</div>
                  <div style={{ fontWeight: 700, fontSize: 15, marginTop: 3 }}>{inv.tenant}</div>
                  <div className="muted" style={{ fontSize: 12 }}>{inv.unitName} · {inv.propName}</div>
                  {inv.tenantRaw && <div className="mono" style={{ fontSize: 10.5, color: "var(--color-text-tertiary)", marginTop: 4, maxWidth: 280 }}>{inv.tenantRaw}</div>}
                </div>
                <div style={{ textAlign: "right" }}>
                  {inv.status === "Void" ? (<>
                    {/* A void owes nothing — never present its historical amount as a balance due. */}
                    <div className="muted" style={{ fontSize: 11 }}>Voided — no balance due</div>
                    <div style={{ fontSize: 26, fontWeight: 700, color: "var(--color-text-tertiary)", textDecoration: inv.total > 0 ? "line-through" : "none" }}>{fmtMoney(inv.total)}</div>
                  </>) : (<>
                    <div className="muted" style={{ fontSize: 11 }}>{inv.status === "Paid" ? "Total (paid)" : "Total due"}</div>
                    <div style={{ fontSize: 26, fontWeight: 700, color: "var(--color-primary)" }}>{fmtMoney(inv.total)}</div>
                    {inv.term && <div className="muted" style={{ fontSize: 11.5 }}>{inv.term}</div>}
                  </>)}
                </div>
              </div>
              <table className="lines" style={{ padding: 0 }}>
                <thead><tr><th style={{ paddingLeft: 18 }}>Description</th><th className="num">Qty</th><th className="num">Rate</th><th className="num" style={{ paddingRight: 18 }}>Amount</th></tr></thead>
                <tbody>
                  {(inv.lines || []).map((l, i) => (
                    <tr key={i}>
                      <td style={{ paddingLeft: 18 }}>{l.desc}
                        {!l.fixed && <div className="ld">Prev read : {fmtHcf(inv.startRead)} HCF   ({fmtShort(inv.periodStart)})
Curr read : {fmtHcf(inv.endRead)} HCF   ({fmtShort(inv.periodEnd)})</div>}
                      </td>
                      <td className="num">{l.qty}</td><td className="num">{fmtMoney(l.rate)}</td><td className="num" style={{ paddingRight: 18 }}>{fmtMoney(l.amount)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot><tr><td colSpan={3} style={{ paddingLeft: 18 }}>Total</td><td className="num" style={{ paddingRight: 18, textDecoration: inv.status === "Void" && inv.total > 0 ? "line-through" : "none" }}>{fmtMoney(inv.total)}</td></tr></tfoot>
              </table>
            </div>

            {inv.memo && (
              <React.Fragment>
                <div className="section-h">Customer memo</div>
                <div className="card" style={{ boxShadow: "none", border: "1px solid var(--color-border-light)", padding: 14, fontSize: 12.5, color: "var(--color-text-secondary)", whiteSpace: "pre-line", lineHeight: 1.5 }}>{inv.memo}</div>
              </React.Fragment>
            )}

            <div className="section-h">Details</div>
            <DRow k="Invoice date">{fmtDate(inv.date)}</DRow>
            <DRow k="Billing period">{fmtShort(inv.periodStart)} → {fmtShort(inv.periodEnd)}</DRow>
            <DRow k="Usage billed">{fmtHcf(inv.usage)} HCF</DRow>
            <DRow k="QBO class">{inv.propName}</DRow>
            {inv.term && <DRow k="Term">{inv.term}</DRow>}
            <DRow k="Manager">{inv.managerName}</DRow>
          </React.Fragment>
        )}
      </div>

      <div className="drawer__foot" style={{ flexWrap: "wrap" }}>
        {ready && inv.status === "Draft" && (
          <label className="row" style={{ flex: "1 1 100%", gap: 8, fontSize: 12.5, cursor: emailEnabled ? "pointer" : "default" }}>
            <input type="checkbox" checked={emailOnPush} disabled={!emailEnabled} onChange={(e) => setEmailOnPush(e.target.checked)} />
            <span>Email invoice to tenant <span className="muted" style={{ fontSize: 11.5 }}>uses the address in QuickBooks{emailEnabled ? "" : " · (email delivery is off in this environment)"}</span></span>
          </label>
        )}
        <span className="muted" style={{ fontSize: 12, flex: 1 }}>{ready && inv.status === "Draft"
          ? "Draft — click Send to post it to QuickBooks."
          : "Already in QuickBooks."}</span>
        {ready && (inv.status === "Open" || inv.status === "Sent") && emailEnabled && <button className="btn btn--ghost btn--sm" disabled={emailing} onClick={emailNow}><Icon name="send" size={14} />{emailing ? "Emailing…" : "Email now"}</button>}
        {/* External: leaves the app for QuickBooks Online — external-link icon + new tab, per the
            design pass. Only rendered when the invoice actually exists in QBO. */}
        {ready && inv.qboInvoiceId && (
          <a className="btn btn--ghost btn--sm" href={`https://qbo.intuit.com/app/invoice?txnId=${encodeURIComponent(inv.qboInvoiceId)}`}
             target="_blank" rel="noopener noreferrer" title={`Opens invoice #${inv.number} in QuickBooks Online (new tab)`}>
            <Icon name="external" size={14} />Open in QuickBooks
          </a>
        )}
        {ready && inv.status === "Draft" && <button className="btn btn--primary" disabled={sending} onClick={send}><Icon name="receipt" size={15} />{sending ? "Sending…" : "Send to QuickBooks"}</button>}
      </div>
    </Drawer>
  );
}

// -------------------------------------------- Create-invoices drawer (replaces the Invoice Run page)
// Runs the live bill preview for a company + read date, lets you deselect units, then creates draft
// invoices — with an optional one-shot "Create + send" that also posts them to QuickBooks.
function CreateInvoicesDrawer({ open, mgr, unitId, initialDate, onClose, onDone }) {
  const toast = useToast();
  const [companies, setCompanies] = useState([]);
  const [scope, setScope] = useState("all");
  const [readDate, setReadDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [rows, setRows] = useState(null);   // preview rows (+sel) · null = loading
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open && initialDate) setReadDate(initialDate); }, [open, initialDate]);   // deep-link (move-out) read date

  useEffect(() => {
    if (!open) return;
    fetchQboConnections(mgr && mgr !== "all" ? mgr : null).then((c) => setCompanies(c || [])).catch(() => setCompanies([]));
  }, [open, mgr]);
  useEffect(() => {
    if (!open) return;
    let ok = true; setRows(null);
    fetchBillPreview(scope === "all" && mgr && mgr !== "all" ? mgr : null, readDate, scope === "all" ? null : scope)
      .then((api) => { if (ok) setRows((api || []).map(mapBillRow).map((r) => ({ ...r, sel: r.eligible }))); })
      .catch(() => { if (ok) setRows([]); });
    return () => { ok = false; };
  }, [open, scope, readDate, mgr]);

  if (!open) return null;
  const eligible = (rows || []).filter((r) => r.eligible && (!unitId || r.unit.id === unitId));
  const chosen = eligible.filter((r) => r.sel);
  const skipped = (rows || []).filter((r) => !r.eligible && (!unitId || r.unit.id === unitId));
  const total = chosen.reduce((s, r) => s + r.comp.total, 0);
  const toggle = (id) => setRows((rs) => rs.map((r) => r.unit.id === id ? { ...r, sel: !r.sel } : r));

  const run = async (alsoSend) => {
    if (!chosen.length) return;
    setBusy(true);
    try {
      const res = await commitBillRun(readDate, chosen.map((r) => ({ tenancyId: r.unit.id, readDate: null, final: !!r.isFinal })));
      const okIds = (res || []).filter((x) => x.ok).map((x) => x.invoiceId);
      let sent = 0;
      if (alsoSend) for (const id of okIds) { try { const s = await sendQboInvoice(id); if (s.sent) sent++; } catch { /* keep going */ } }
      setBusy(false); onDone(); onClose();
      toast({ title: okIds.length ? (alsoSend ? "Invoices created and sent" : "Draft invoices created") : "Nothing billed",
              body: okIds.length
                ? `${okIds.length} draft${okIds.length === 1 ? "" : "s"} created${alsoSend ? ` · ${sent} sent to QuickBooks` : " — select and Send to QuickBooks when ready"}.`
                : "No units had new reads to bill for this period.",
              err: !okIds.length });
    } catch { setBusy(false); toast({ title: "Run failed", body: "The API rejected the run.", err: true }); }
  };

  return (
    <Drawer open={open} onClose={onClose} wide>
      <div className="drawer__head">
        <div className="av av-sq" style={{ background: "var(--accent)" }}><Icon name="receipt" size={20} /></div>
        <div style={{ flex: 1 }}><h2>Create invoices</h2><div className="sub">Bill this period's water usage — saved as drafts you can review before sending</div></div>
        <button className="drawer__close" onClick={onClose}>&times;</button>
      </div>
      <div className="drawer__body">
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 14 }}>
          <div className="field"><label>Company</label>
            <select className="select" value={scope} onChange={(e) => setScope(e.target.value)}>
              <option value="all">All companies</option>
              {companies.map((c) => <option key={c.realmId} value={c.realmId}>{c.companyName || `Realm ${c.realmId}`}</option>)}
            </select></div>
          <div className="field"><label>Billing period end</label><input className="input" type="date" value={readDate} onChange={(e) => setReadDate(e.target.value)} />
            <span className="muted" style={{ fontSize: 11, marginTop: 4, display: "block", lineHeight: 1.4 }}>Bills usage through this date. Each unit's period starts at its previous reading (shown below).</span>
          </div>
        </div>
        <div className="section-h" style={{ marginTop: 0 }}>Units to bill · {chosen.length} of {eligible.length} eligible</div>
        <table className="lines">
          <thead><tr><th style={{ width: 28 }}></th><th>Tenant / unit</th><th>Billing period</th><th className="num">Usage</th><th className="num">Amount</th></tr></thead>
          <tbody>
            {rows === null && <tr><td colSpan={5}><div className="muted" style={{ padding: "6px 0" }}>Loading preview…</div></td></tr>}
            {rows && eligible.length === 0 && <tr><td colSpan={5}><div className="muted" style={{ padding: "6px 0" }}>No units have new reads to bill for this date.</div></td></tr>}
            {eligible.map((r) => (
              <tr key={r.unit.id}>
                <td onClick={(e) => e.stopPropagation()}><input type="checkbox" checked={r.sel} onChange={() => toggle(r.unit.id)} /></td>
                <td><div className="cell-2"><b>{r.unit.tenant}</b><span>{r.unit.name}</span></div></td>
                <td className="muted" style={{ fontSize: 11.5, whiteSpace: "nowrap" }}>{r.prior ? fmtShort(r.prior.date) : "—"} → {r.end ? fmtShort(r.end) : "—"}</td>
                <td className="num">{r.usage}</td>
                <td className="num strong">{fmtMoney(r.comp.total)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {skipped.length > 0 && (
          <details style={{ marginTop: 12 }}>
            <summary className="muted" style={{ fontSize: 12, cursor: "pointer" }}>{skipped.length} unit{skipped.length === 1 ? "" : "s"} not billed this run — see why</summary>
            <table className="lines" style={{ marginTop: 6 }}>
              <tbody>
                {skipped.map((r) => (
                  <tr key={r.unit.id}>
                    <td><div className="cell-2"><b>{r.unit.tenant}</b><span>{r.unit.name}</span></div></td>
                    <td className="muted" style={{ fontSize: 11.5, textAlign: "right", whiteSpace: "nowrap" }}>{r.skip || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </details>
        )}
      </div>
      <div className="drawer__foot">
        <span className="muted" style={{ fontSize: 12, flex: 1 }}>{chosen.length} invoice{chosen.length === 1 ? "" : "s"} · {fmtMoney(total)}</span>
        <button className="btn btn--ghost" onClick={onClose}>Cancel</button>
        <button className="btn btn--soft" disabled={busy || !chosen.length} onClick={() => run(true)}>{busy ? "Working…" : "Create + send to QuickBooks"}</button>
        <button className="btn btn--primary" disabled={busy || !chosen.length} onClick={() => run(false)}><Icon name="plus" size={15} />{busy ? "Working…" : `Create ${chosen.length} draft${chosen.length === 1 ? "" : "s"}`}</button>
      </div>
    </Drawer>
  );
}

export { Invoices };
